import React from 'react';
import classNames from 'classnames';
import { FusionSectionText, FusionSectionTitle } from '@benzinga/ui';
import { Button } from '@benzinga/core-ui';
import styled from '@benzinga/themetron';
import { WordpressPost } from '@benzinga/content-manager';
import { sanitizeHTML } from '@benzinga/frontend-utils';

interface Props {
  card?: WordpressPost;
  className?: string;
  variant?: 'large' | 'default' | 'compact' | 'list';
}

export const NewsCardItem = ({ card, className, variant = 'large' }: Props) => {
  if (!card) return null;
  const author_name = card?.author?.name ?? '';

  switch (variant) {
    case 'large':
      return (
        <CardWrapper className={classNames(variant, className)}>
          <div className="image-wrapper">
            {card?.image && <img alt={card.title} className="image" src={card.image as string} />}
          </div>
          <div className="content-wrapper">
            <div className="card-info-wrapper">
              <p className="author-name">{author_name}</p>
              <span>•</span>
              <p>{card.date}</p>
            </div>
            <a href={card.url}>
              <FusionSectionTitle className="card-title">
                {card.title && <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.title) }} />}
              </FusionSectionTitle>
            </a>
            {card.description && (
              <div
                className="card-description"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.description) }}
              ></div>
            )}
            <Button as="a" className="actionButton" href={card.url} size="fusion-md" variant="flat-blue">
              read more
            </Button>
          </div>
        </CardWrapper>
      );
    case 'compact':
      return (
        <CardWrapper className={classNames(variant, className)}>
          {card?.image && <img alt={card.title} className="image" src={card.image as string} />}
          <a href={card.url}>
            <FusionSectionText className="card-description">
              {card.title && <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.title) }} />}
            </FusionSectionText>
          </a>
        </CardWrapper>
      );

    case 'list':
      return (
        <CardWrapper className={classNames(variant, className)}>
          <a href={card.url}>
            <FusionSectionText>
              {card.title && <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.title) }} />}
            </FusionSectionText>
          </a>
        </CardWrapper>
      );

    case 'default':
      return (
        <CardWrapper className={classNames(variant, className)}>
          <a href={card.url}>{card?.image && <img alt={card.title} className="image" src={card.image as string} />}</a>
          <div className="content-wrapper">
            <a href={card.url}>
              <FusionSectionTitle className="card-title">
                {card.title && <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.title) }} />}
              </FusionSectionTitle>
            </a>
            <div className="card-info-wrapper">
              <p className="author-name">{author_name}</p>
              <span>•</span>
              <p className="created-date">{card.date}</p>
            </div>
            {card.description && (
              <div
                className="card-description"
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(card.description) }}
              ></div>
            )}
          </div>
        </CardWrapper>
      );
    default:
      return null;
  }
};

const CardWrapper = styled.div`
  &.list {
    padding: 10px 12px;
    border-bottom: 1px solid #ceddf2;
    a {
      color: #2d2d2d;
      p {
        color: #2d2d2d;
        margin-bottom: 0;
        font-size: 14px;
        line-height: 20px;
      }
    }
  }
  &.large {
    position: relative;
    padding: 24px;
    border: 1px solid #e1ebfa;
    border-radius: 4px;
    background-position: top right;
    background-size: contain;
    background-repeat: no-repeat;
    background-color: #f2f8ff;

    .image-wrapper {
      position: absolute;
      display: block;
      top: 0;
      right: 0;
      height: 100%;
      max-width: 100%;
      overflow: hidden;

      &:after {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, #f2f8ff 0%, #f2f8ff 20%, transparent 100%);
      }
    }

    .content-wrapper {
      position: relative;
      z-index: 1;
    }

    .image {
      max-width: unset;
      height: 100%;
      width: auto;
    }

    .card-title {
      margin-top: 72px;
      max-width: 498px;
    }

    .card-description {
      margin-top: 16px;
      max-width: 340px;
    }

    .actionButton {
      margin-top: 40px;
      text-transform: uppercase;
    }
  }

  &.compact {
    display: flex;
    align-items: center;
    gap: 16px;
    border-radius: 4px;
    transition: 0.25s background-color ease-in-out;
    cursor: pointer;

    &:hover {
      background-color: #3f83f80d;
    }

    .image {
      display: block;
      flex-shrink: 0;
      width: 56px;
      height: 56px;
      border-radius: 4px;
      object-fit: cover;
    }

    .card-description {
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
    }
  }

  &.default {
    overflow: hidden;
    border: 1px solid #e1ebfa;
    border-radius: 4px;

    @media screen and (max-width: 959px) {
      min-width: 352px;
    }

    @media screen and (max-width: 567px) {
      min-width: 296px;
    }

    .content-wrapper {
      padding: 16px 24px;
    }

    .image {
      width: 100%;
      height: 172px;
      object-fit: cover;
    }

    .card-title {
      font-size: 24px;
      line-height: 32px;
      margin-bottom: 16px;
    }

    .card-info-wrapper {
      margin-bottom: 16px;
    }

    .card-title,
    .card-description {
      display: -webkit-box;
      text-overflow: ellipsis;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
    }
  }

  .card-info-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    color: #99aecc;
    @media screen and (max-width: 567px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      span {
        display: none;
      }
    }
  }

  .author-name {
    color: #395173;
  }
`;
